// ---------------------------------------------------------------------------
// Lightweight client-side User-Agent parser — no external dependency
// Detects OS, browser, and device type from navigator.userAgent
// ---------------------------------------------------------------------------

export interface ParsedUA {
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

export function parseUserAgent(ua: string): ParsedUA {
  const s = ua || '';

  // Device type
  const isTablet = /ipad|tablet|(android(?!.*mobile))/i.test(s);
  const isMobile = !isTablet && /mobile|iphone|ipod|android|blackberry|windows phone/i.test(s);
  const deviceType: ParsedUA['deviceType'] = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  // OS detection
  let os = 'Unknown';
  let osVersion = '';
  if (/windows nt/i.test(s)) {
    os = 'Windows';
    const m = s.match(/windows nt ([\d.]+)/i);
    const ntMap: Record<string, string> = {
      '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7', '6.0': 'Vista', '5.1': 'XP',
    };
    osVersion = m ? (ntMap[m[1]] || m[1]) : '';
  } else if (/mac os x/i.test(s)) {
    os = 'macOS';
    const m = s.match(/mac os x ([\d_]+)/i);
    osVersion = m ? m[1].replace(/_/g, '.') : '';
  } else if (/ipad/i.test(s)) {
    os = 'iPadOS';
    const m = s.match(/os ([\d_]+)/i);
    osVersion = m ? m[1].replace(/_/g, '.') : '';
  } else if (/iphone|ipod/i.test(s)) {
    os = 'iOS';
    const m = s.match(/os ([\d_]+)/i);
    osVersion = m ? m[1].replace(/_/g, '.') : '';
  } else if (/android/i.test(s)) {
    os = 'Android';
    const m = s.match(/android ([\d.]+)/i);
    osVersion = m ? m[1] : '';
  } else if (/linux/i.test(s)) {
    os = 'Linux';
  } else if (/cros/i.test(s)) {
    os = 'ChromeOS';
  }

  // Browser detection (order matters — Edge must come before Chrome)
  let browser = 'Unknown';
  let browserVersion = '';
  if (/edg\//i.test(s)) {
    browser = 'Edge';
    const m = s.match(/edg\/([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  } else if (/opr\//i.test(s) || /opera/i.test(s)) {
    browser = 'Opera';
    const m = s.match(/(?:opr|opera)[/ ]([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  } else if (/firefox\//i.test(s)) {
    browser = 'Firefox';
    const m = s.match(/firefox\/([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  } else if (/chrome\//i.test(s)) {
    browser = 'Chrome';
    const m = s.match(/chrome\/([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  } else if (/safari\//i.test(s)) {
    browser = 'Safari';
    const m = s.match(/version\/([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  } else if (/msie|trident/i.test(s)) {
    browser = 'IE';
    const m = s.match(/(?:msie |rv:)([\d.]+)/i);
    browserVersion = m ? m[1] : '';
  }

  return { os, osVersion, browser, browserVersion, deviceType };
}
